const MAX_ENRICH_IMAGE_BYTES = 2 * 1024 * 1024;
const ENRICH_IMAGE_MAX_EDGE = 1600;

export { MAX_ENRICH_IMAGE_BYTES, ENRICH_IMAGE_MAX_EDGE };

/**
 * Clipboard Files sometimes arrive with an empty `type` even when the item
 * MIME is `image/png`. tldraw rejects those in runFileChecks ("No mime type").
 * @param {Blob|File|null} blob
 * @param {string} mimeType
 * @returns {File|null}
 */
export function normalizeClipboardImageFile(blob, mimeType = '') {
  if (!blob) return null;
  const type = String(mimeType || blob.type || '').trim().toLowerCase();
  if (!type.startsWith('image/')) return null;
  const ext = type.split('/')[1]?.split('+')[0] || 'png';
  if (blob instanceof File && blob.type === type) return blob;
  return new File([blob], `clipboard.${ext}`, { type });
}

export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read image'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Downscale/compress a clipboard image so enrich-clipboard stays under the
 * server base64 cap. Returns null when compression cannot get under maxBytes.
 * @param {Blob|File} file
 * @param {number} [maxBytes]
 * @returns {Promise<File|null>}
 */
export async function prepareImageFileForEnrich(file, maxBytes = MAX_ENRICH_IMAGE_BYTES) {
  if (!file) return null;
  if (file.size <= maxBytes) {
    return file instanceof File
      ? file
      : new File([file], 'clipboard.png', { type: file.type || 'image/png' });
  }
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    return null;
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }

  try {
    let scale = Math.min(1, ENRICH_IMAGE_MAX_EDGE / Math.max(bitmap.width, bitmap.height, 1));
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0, w, h);

      // Re-encode at lower quality, then shrink dimensions if still too large.
      // Sequential toBlob calls are intentional (each depends on prior size).
      let quality = 0.82;
      let blob = null;
      while (quality >= 0.4) {
        blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, 'image/jpeg', quality);
        });
        if (blob && blob.size <= maxBytes) {
          return new File([blob], 'clipboard-enrich.jpg', { type: 'image/jpeg' });
        }
        quality -= 0.14;
      }
      scale *= 0.7;
    }
    return null;
  } finally {
    bitmap.close?.();
  }
}
